import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TrashIcon from '@wix/da-ds/pkg/Icons/16x16/Trashcan';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { emptyDocument } from '@wix/da-editor-tiptap/pkg/components/utils/helpers';
import {
  Question,
  QuestionFieldName,
  QuestionFormFields,
} from '../../../../types';
import QuestionField from '../../../../fields/QuestionField';
import AnswerTypeField from '../../../../fields/AnswerTypeField';
import AnswerChoicesGroupField from '../../../../fields/AnswerChoicesGroupField';
import QuestionIsRequiredField from '../../../../fields/QuestionIsRequiredField';
import { useQuestionFormValidation } from '../../../../validation';

import s from './QuestionForm.scss';

interface Props {
  question?: Question;
  onSave: (question: QuestionFormFields) => void;
  onDiscard: () => void;
}

const QuestionForm: React.FC<Props> = ({ question, onSave, onDiscard }) => {
  const { t } = useTranslation();
  const validationSchema = useQuestionFormValidation();
  const formContext = useForm<QuestionFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [QuestionFieldName.QUESTION_BODY]: question?.question ?? emptyDocument(),
      [QuestionFieldName.QUESTION_TYPE]: question?.type ?? 'free_text',
      [QuestionFieldName.IS_REQUIRED]: question?.isRequired ?? false,
      [QuestionFieldName.ANSWER_CHOICES]: question?.answers || [
        {
          answer: '',
          priceModifier: undefined,
          position: 0,
        },
      ],
    },
    resolver: yupResolver(validationSchema),
  });

  const questionType = formContext.watch(QuestionFieldName.QUESTION_TYPE);

  const onSaveClick = useCallback(() => {
    formContext.handleSubmit(onSave)();
  }, [formContext, onSave]);

  return (
    <FormProvider {...formContext}>
      <div className={s['root']}>
        <div className={s['header']}>
          <Heading className={s['heading']}>
            {question
              ? t('originalDeviationPopup.questionnaire.form.heading.edit')
              : t('originalDeviationPopup.questionnaire.form.heading.add')}
          </Heading>
          <TextButton
            variant="edit-purple"
            size="medium"
            icon={TrashIcon}
            onClick={onDiscard}
          >
            {t('common.discard')}
          </TextButton>
        </div>
        <hr className={s['divider']} />

        <div className={s['fields']}>
          <QuestionField />
          <AnswerTypeField />
        </div>

        {['single_choice', 'multiple_choice'].includes(questionType) && (
          <AnswerChoicesGroupField questionType={questionType} />
        )}

        <hr className={s['divider']} />
        <div className={s['footer']}>
          <QuestionIsRequiredField />
          <SolidButton size="small" variant="edit-purple" onClick={onSaveClick}>
            {t('originalDeviationPopup.questionnaire.form.save')}
          </SolidButton>
        </div>
      </div>
    </FormProvider>
  );
};
QuestionForm.displayName = 'QuestionForm';

export default QuestionForm;
